import { call, put, select, takeLatest } from 'redux-saga/effects'
import { throwPcrException } from '../../../pcr/store/pcr.actions'
import { getCmdChartDefaultsSelector, getDatapointsConfigSelector } from '../../../configurations/selectors'
import { ICmdFacChartDefaults, IDataPointsConfig } from '../../../configurations/store/configurations.types'
import { setMotorSensory } from '../motorSensoryAssessment/store/motoSensoryAssessment.actions'
import { getNeuroOptionsSelector } from '../../../options/selectors'
import { setDefaultValue } from '../../../../utilities/data.util'
import { INeuroOptionsState } from '../../../options/store/options.types'
import { setPupils } from '../pupils/store/pupils.actions'
import { setNeuro } from '../store/neuro.actions'
import { getServiceIdSelector } from '../../../user/selectors'
import { PcrDocsService } from '../../../../services/pcrDocs.service'
import { IPcr } from '../../../pcr/store/pcr.types'
import { getPcrIdSelector } from '../../../home/selectors'
import { performWithMutexHOG } from '../../../../services/mutexUtility.service'
import { InitialAssessmentConstants } from '../../store/initialAssessment.constants'

export function* setNeuroDefaults() {
  const serviceId: string = yield select(getServiceIdSelector)
  const pcrDocsService = new PcrDocsService(serviceId)
  const pcrID: string = yield select(getPcrIdSelector)
  if (pcrID) {
    const pcrDoc: IPcr = yield call([pcrDocsService, pcrDocsService.getPCR], pcrID)
    if (!pcrDoc.prNabc) {
      const datapointsConfig: IDataPointsConfig = yield select(getDatapointsConfigSelector)
      const cmdChartDefaults: ICmdFacChartDefaults = yield select(getCmdChartDefaultsSelector)
      const neuroOptions: INeuroOptionsState = yield select(getNeuroOptionsSelector)
      const {
        DP_SHOWDEFAULTS,
        DP_MOTOR_PICKLIST,
        DP_MOTOR_COMMENTS,
        DP_SENSORY_COMMENTS,
        DP_SENSORY_PICKLIST,
        DP_PUPIL_PICKLIST,
        DP_PUPIL_COMMENTS,
        DP_PUPIL_SIZELIST,
      } = datapointsConfig
      const {
        NEURO_MOTOR,
        NEURO_SENSORY,
        MOTOR_LA,
        MOTOR_LL,
        MOTOR_RA,
        MOTOR_RL,
        SENSORY_LA,
        SENSORY_LL,
        SENSORY_RA,
        SENSORY_RL,
        PUPIL_REACT,
        PUPIL_RX_L,
        PUPIL_RX_R,
        PUPIL_SIZE_L,
        PUPIL_SIZE_R,
        HEAD_CONSCIOUSNESS,
      } = cmdChartDefaults

      const { motorOptions, pupilsReactOptions, pupilsSizeOptions, headConciousnessOptions, sensoryOptions } =
        neuroOptions

      try {
        if (DP_SHOWDEFAULTS) {
          const motorLa = setDefaultValue(motorOptions, MOTOR_LA, DP_MOTOR_PICKLIST)
          const motorRa = setDefaultValue(motorOptions, MOTOR_RA, DP_MOTOR_PICKLIST)
          const motorLl = setDefaultValue(motorOptions, MOTOR_LL, DP_MOTOR_PICKLIST)
          const motorRl = setDefaultValue(motorOptions, MOTOR_RL, DP_MOTOR_PICKLIST)

          const sensoryLa = setDefaultValue(sensoryOptions, SENSORY_LA, DP_SENSORY_PICKLIST)
          const sensoryLl = setDefaultValue(sensoryOptions, SENSORY_LL, DP_SENSORY_PICKLIST)
          const sensoryRa = setDefaultValue(sensoryOptions, SENSORY_RA, DP_SENSORY_PICKLIST)
          const sensoryRl = setDefaultValue(sensoryOptions, SENSORY_RL, DP_SENSORY_PICKLIST)

          const pupilRxL = setDefaultValue(pupilsReactOptions, PUPIL_RX_L, DP_PUPIL_PICKLIST)
          const pupilRxR = setDefaultValue(pupilsReactOptions, PUPIL_RX_R, DP_PUPIL_PICKLIST)
          const pupilSizeL = setDefaultValue(pupilsSizeOptions, PUPIL_SIZE_L, DP_PUPIL_SIZELIST)
          const pupilSizeR = setDefaultValue(pupilsSizeOptions, PUPIL_SIZE_R, DP_PUPIL_SIZELIST)

          const headConciousness = setDefaultValue(headConciousnessOptions, HEAD_CONSCIOUSNESS)

          const motorComments = DP_MOTOR_COMMENTS ? NEURO_MOTOR : ''
          const headSensory = DP_SENSORY_COMMENTS ? NEURO_SENSORY : ''
          const motorSensoryItems = {
            motorLa,
            motorRa,
            motorLl,
            motorRl,
            sensoryLa,
            sensoryLl,
            sensoryRa,
            sensoryRl,
            motorComments,
            headSensory,
          }
          yield put(setMotorSensory(motorSensoryItems))

          const headReactive = DP_PUPIL_COMMENTS ? PUPIL_REACT : ''
          const pupilsItems = { pupilRxL, pupilRxR, pupilSizeL, pupilSizeR, headReactive }
          yield put(setPupils(pupilsItems))

          const neuroItems = { headConciousness }
          yield put(setNeuro(neuroItems))
        }
      } catch (e) {
        yield put(throwPcrException(`setNeuroDefaults() error: ${e}`))
      }
    }
  }
}

export function* setNeuroDefaultsSaga() {
  yield takeLatest([InitialAssessmentConstants.SET_PR_NABC_DEFAULTS], performWithMutexHOG, setNeuroDefaults)
}
